import { ehConcluido } from './statusDoChamado';

/**
 * Serviço e Categoria: quando o chamado não pode ficar sem eles.
 *
 * ## O pedido (ponto 4 da Gisele, 15/10/2026)
 *
 * O chamado que nasce do chat vem sem classificação — o cliente escolhe um
 * DEPARTAMENTO na conversa, e isso não é serviço nem categoria. A decisão dele
 * em 16/10: a triagem preenche os dois, à mão.
 *
 * ## Onde a exigência cai, e por que não em todo salvamento
 *
 * A tela do chamado não tem botão "Salvar": cada mudança grava sozinha
 * (trocar responsável, trocar status, vincular cliente). Travar qualquer uma
 * delas por falta de categoria faria o operador perder a mudança que acabou
 * de fazer, por causa de um campo que ele ia preencher depois.
 *
 * Por isso a exigência cai no momento em que a classificação realmente deixa
 * de poder esperar: **ao finalizar** (Resolvido, Fechado ou Cancelado). Dá
 * para trabalhar o chamado sem classificar; não dá para encerrá-lo assim, que
 * é justamente quando o dado passa a valer para relatório.
 *
 * ## Só o chamado que veio do chat
 *
 * São centenas de milhares de chamados antigos sem serviço ou categoria.
 * Exigir de todos travaria o fechamento da base inteira por uma regra que foi
 * pedida para a conversa. Na criação manual o formulário já exige Serviço, e
 * agora exige Categoria junto.
 */

/** Os canais que a sala gera -- a mesma lista que `Ticket::canalDaSala` grava. */
const CANAIS_DO_CHAT = ['chat', 'whatsapp'];

/**
 * O chamado nasceu de uma conversa?
 *
 * `room_id` é o vínculo direto. O canal entra como segunda fonte porque o
 * chamado pode ter sido aberto à mão durante o atendimento e vinculado à sala
 * depois -- e porque chamado antigo pode ter o canal e não o vínculo.
 */
export const ehChamadoDoChat = (chamado) => {
    if (chamado?.room_id) return true;

    const canal = String(chamado?.channel || '').trim().toLowerCase();

    return CANAIS_DO_CHAT.includes(canal);
};

/**
 * A regra de quando ninguém configurou nada.
 *
 * É o comportamento que existia antes dos pontos 17 e 31 (22/10/2026), e o
 * mesmo padrão do `TicketConclusionRule::PADRAO` no servidor. Vale também
 * quando a leitura da configuração FALHA: nesse caso a tela avisa pelo padrão,
 * e o pior que acontece é avisar sobre um campo que o servidor deixaria passar
 * — o contrário (não avisar e levar 422) é que seria ruim.
 */
export const REGRA_PADRAO = {
    campos: ['service_id', 'category'],
    exigir_de_todos_os_canais: false,
    suportados: { service_id: 'serviço', category: 'categoria' },
};

/** Id e texto se esvaziam de formas diferentes -- espelha o `vazio()` da api. */
const vazio = (campo, valor) => {
    const ehIdentificador = campo === 'assigned_to' || campo.endsWith('_id');

    if (ehIdentificador) {
        // A tela manda 0 no seletor em branco, então zero é vazio. Já uma
        // categoria chamada "0" é um valor que alguém escolheu.
        return valor == null || valor === '' || Number(valor) <= 0;
    }

    return !String(valor ?? '').trim();
};

/**
 * O que falta classificar, em palavras que cabem na mensagem de erro.
 *
 * @param {object} chamado
 * @param {object} [regra]  o que veio de `/regra-de-conclusao`
 */
export const classificacaoPendente = (chamado, regra = REGRA_PADRAO) => {
    const campos = Array.isArray(regra?.campos) ? regra.campos : REGRA_PADRAO.campos;
    const nomes = regra?.suportados || REGRA_PADRAO.suportados;

    return campos
        .filter((campo) => vazio(campo, chamado?.[campo]))
        // Sem nome conhecido o campo não entra: a mensagem "falta " seguida de
        // nada não ajuda ninguém, e quem GARANTE a regra é o servidor.
        .map((campo) => nomes[campo])
        .filter(Boolean);
};

/**
 * O que impede ESTE salvamento, se é que impede algo.
 *
 * Devolve lista vazia quando pode salvar. Só diz não quando as três coisas
 * valem ao mesmo tempo: o chamado veio do chat, o salvamento o está
 * finalizando, e falta classificação.
 *
 * @param {object} chamado  o estado que está sendo gravado
 * @param {number[]} finalizados  ids dos status finais (`idsFinalizados`)
 */
export const impedimentoParaFinalizar = (chamado, finalizados, regra = REGRA_PADRAO) => (
    impedimentoAoFinalizar(chamado, ehConcluido(chamado, finalizados), regra)
);

/**
 * O mesmo impedimento, com o "vai finalizar" vindo de FORA.
 *
 * Espelha a assinatura do servidor (`ClassificacaoParaFinalizar::impedimento`,
 * que recebe `bool $vaiFinalizar`), e existe porque nem toda tela descobre o
 * fechamento do mesmo jeito.
 *
 * O "Adicionar ação" já sabe que está fechando por outro caminho -- ele lê o
 * `status` legado (3 ou 4) junto do `status_id` --, e é esse sinal que decide
 * se vai pedir o motivo no comentário. Recalcular aqui por `ehConcluido`
 * daria resposta DIFERENTE: escolher "Resolvido" no "Fechar ticket..." grava
 * o `status` legado na hora, mas o `status_id` só muda se a lista de status
 * já tiver chegado da api. Com duas leituras do mesmo fato, uma guarda
 * dispararia e a outra não.
 *
 * @param {object} chamado
 * @param {boolean} vaiFinalizar
 */
export const impedimentoAoFinalizar = (chamado, vaiFinalizar, regra = REGRA_PADRAO) => {
    if (!vaiFinalizar) return [];
    if (!regra?.exigir_de_todos_os_canais && !ehChamadoDoChat(chamado)) return [];

    return classificacaoPendente(chamado, regra);
};
